import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  forwardRef,
  inject,
  input,
  model,
  viewChild,
} from '@angular/core';

import { RadioGroupComponent, RadioItem, UI_RADIO_GROUP, UI_RADIO_ITEM } from './radio-group.component';

export interface DayChip {
  id: string;
  /** "Hoy", "Mié"… */
  weekday: string;
  /** "29" */
  day: string;
  /** La fecha completa para el lector de pantalla: "Hoy, jueves 29 de octubre". */
  fullLabel: string;
  disabled?: boolean;
}

/** Una ficha de día. Interna de `ui-day-chips`. */
@Component({
  selector: 'ui-day-chip',
  template: `
    <button
      #control
      type="button"
      role="radio"
      class="chip"
      [class.selected]="checked()"
      [class.disabled]="disabled()"
      [attr.aria-checked]="checked()"
      [attr.aria-disabled]="disabled() ? 'true' : null"
      [attr.aria-label]="fullLabel()"
      [attr.tabindex]="tabbable() ? 0 : -1"
      (click)="group.select(this)"
    >
      <span class="weekday">{{ weekday() }}</span>
      <span class="day">{{ day() }}</span>
    </button>
  `,
  styleUrl: './day-chips.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: UI_RADIO_ITEM, useExisting: forwardRef(() => DayChipComponent) }],
})
export class DayChipComponent implements RadioItem {
  // Ver la nota en RadioRowComponent: no es `required` porque va dentro de un @for.
  readonly value = input('');
  readonly weekday = input.required<string>();
  readonly day = input.required<string>();
  readonly fullLabel = input.required<string>();
  readonly disabled = input(false, { transform: booleanAttribute });

  protected readonly group = inject(UI_RADIO_GROUP);
  protected readonly checked = computed(() => this.group.value() === this.value());
  protected readonly tabbable = computed(() => this.group.tabbable() === this.value());

  private readonly control = viewChild.required<ElementRef<HTMLButtonElement>>('control');

  focus(): void {
    this.control().nativeElement.focus();
  }
}

/**
 * Tira de días. Cuántos días y cuáles los decide quien la usa: en el API es `days_to_book` por campus,
 * y los "3 días" del diseño son sólo un ejemplo. Con más días, la tira baja a otro renglón.
 */
@Component({
  selector: 'ui-day-chips',
  imports: [RadioGroupComponent, DayChipComponent],
  template: `
    <ui-radio-group class="days" [label]="label()" [(value)]="value">
      @for (chip of days(); track chip.id) {
        <ui-day-chip
          [value]="chip.id"
          [weekday]="chip.weekday"
          [day]="chip.day"
          [fullLabel]="chip.fullLabel"
          [disabled]="chip.disabled ?? false"
        />
      }
    </ui-radio-group>
  `,
  styles: `
    .days {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(3.25rem, 1fr));
      gap: var(--ui-space-2);
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DayChipsComponent {
  readonly label = input.required<string>();
  readonly days = input.required<readonly DayChip[]>();
  readonly value = model<string | null>(null);
}
